/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – SimulatorContext.tsx — FINAL
// Context global + toast automat + updateLOD + updateInterval
// ============================================================

import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { notification } from 'antd';
import { FireOutlined, WarningOutlined, AlertOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { useSimulator, SimulatorState, setLodOverride, setIntervalOverride } from '../hooks/useSimulator';
import { Alert, SensorHistory } from '../types';

// ─── Interfața contextului ────────────────────────────────
interface SimulatorContextType extends SimulatorState {
  pause:          () => void;
  resume:         () => void;
  forceTick:      () => void;
  updateLOD:      (sensorId: string, newLod: number) => void;
  updateInterval: (seconds: number) => void;
  getLiveHistory: (sensorId: string) => SensorHistory | undefined;
}

const SimulatorContext = createContext<SimulatorContextType | null>(null);

const SEV_CONFIG = {
  critical: { color: '#ef4444', icon: <FireOutlined    style={{ color: '#ef4444' }} /> },
  high:     { color: '#f97316', icon: <WarningOutlined  style={{ color: '#f97316' }} /> },
  medium:   { color: '#f59e0b', icon: <AlertOutlined    style={{ color: '#f59e0b' }} /> },
  low:      { color: '#84cc16', icon: <InfoCircleOutlined style={{ color: '#84cc16' }} /> },
};

// ─── Provider ────────────────────────────────────────────
export const SimulatorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const simulator = useSimulator(30_000);

  // Toast automat la alertă nouă
  useEffect(() => {
    if (!simulator.newAlertIds.length) return;
    simulator.newAlertIds.forEach(alertId => {
      const alert = simulator.alerts.find((a: Alert) => a.id === alertId);
      if (!alert) return;
      const cfg = SEV_CONFIG[alert.severity] ?? SEV_CONFIG.low;
      api.open({
        key: alertId, type: 'error', icon: cfg.icon,
        message: (
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            {alert.severity === 'critical' ? '🔴' : alert.severity === 'high' ? '🟠' : '🟡'}{' '}
            Alertă {alert.severity === 'critical' ? 'critică' : alert.severity === 'high' ? 'ridicată' : 'medie'}
          </span>
        ),
        description: (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{alert.sensorName}</div>
            <div style={{ color: '#64748b' }}>
              {alert.parameter}:{' '}
              <span style={{ color: cfg.color, fontWeight: 700 }}>{alert.value} {alert.unit}</span>
              {' '}(LOD: {alert.lod} {alert.unit})
            </div>
            <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 3 }}>{alert.location}</div>
          </div>
        ),
        duration: 8, placement: 'bottomRight',
        style: { borderRadius: 12, border: `1px solid ${cfg.color}30`, boxShadow: `0 4px 20px ${cfg.color}20` },
      });
    });
  }, [simulator.newAlertIds]);

  // Funcții expuse
  const updateLOD = useCallback((sensorId: string, newLod: number) => {
    setLodOverride(sensorId, newLod);
  }, []);

  const updateInterval = useCallback((seconds: number) => {
    setIntervalOverride(seconds * 1000);
  }, []);

  const getLiveHistory = useCallback((sensorId: string): SensorHistory | undefined => {
    return simulator.liveHistory.find(h => h.sensorId === sensorId);
  }, [simulator.liveHistory]);

  return (
    <SimulatorContext.Provider value={{
      ...simulator,
      updateLOD,
      updateInterval,
      getLiveHistory,
    }}>
      {contextHolder}
      {children}
    </SimulatorContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────
export const useSimulatorData = (): SimulatorContextType => {
  const ctx = useContext(SimulatorContext);
  if (!ctx) throw new Error('useSimulatorData trebuie folosit în SimulatorProvider');
  return ctx;
};